import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Column, Row, Select } from "../../../../components";
import { RootState } from "../../../../shared/store";
import { FloatingLabel, Form } from "react-bootstrap";
import { PolicyCoverageReadDto } from "../../../model/PolicyCoverageReadDto";
import LookupService from "../../../../Services/LookupService";
import { SelectOption } from "../../../../shared/components/input";
import { updateSelectedPolicyDetails } from "../../../customer-slice/Customer-slice";
import PolicyDetailService from "../../customer-edit-policy/CustomerEditPolicyDetailsService";
import { CommonUtility } from "../../../../Services/CommonUtility";
import { PolicyCoverageDto } from "../../../model/PolicyCoverageDto";
import { CUSTOMER_POLICY_LOBCodeId } from "../../../../shared/util/constant";

export interface ICustomerBasicCoverageForm {
    customerId?: string;
    coverageId?: string;
    polEventId?: string;
    polId?: string;
    customerBasicCoverage: PolicyCoverageReadDto; 
    setIsFormSaved?: (isSaved: boolean) => void;
    deductibleTypesOptions?: SelectOption[];
    isCoverage?: boolean; // Optional prop to indicate if this is for coverages or endorsements
    formValidation?: any;
    tabIndex?: number;
    lobCodeId?: string;
}

const CustomerBasicCoverageForm: React.FunctionComponent<ICustomerBasicCoverageForm> = (props) => {
    const { customerBasicCoverage, setIsFormSaved, coverageId, polEventId, polId, deductibleTypesOptions, isCoverage, formValidation: externalFormValidation, lobCodeId, } = props;
    let tbIndex = props.tabIndex;

    const isEarthquake = Array.isArray(CUSTOMER_POLICY_LOBCodeId.EARTHQUAKE) 
      ? CUSTOMER_POLICY_LOBCodeId.EARTHQUAKE.includes(lobCodeId) 
      : lobCodeId === CUSTOMER_POLICY_LOBCodeId.EARTHQUAKE;
    const dispatch = useDispatch();

    // Get user name from context
    const userDetails = useSelector((state: RootState) => state.userContextSlice);
    const userName = `${userDetails?.userContext?.firstName} ${userDetails?.userContext?.lastName}`;
    const customerDetail = useSelector((state: RootState) => state?.customerSlice);
    const policyDetailKey = CommonUtility.getPolicyDetailKey(polId, polEventId);
    const policyDetails = customerDetail.selectedPolicyDetails?.[policyDetailKey];
    const policyDetailService = new PolicyDetailService();

    // State for lookup data/ dropdowns
    const [coverageTypes, setCoverageTypes] = useState<SelectOption[]>([]);
    const [deductibleTypes, setDeductibleTypes] = useState<SelectOption[]>([]);
    
    const [formValidation, setFormValidation] = useState<any>(externalFormValidation || {});

    useEffect(() => {
        if (externalFormValidation) {
            setFormValidation(externalFormValidation);
        }
    }, [externalFormValidation]);
    const [coverageData, setCoverageData] = useState<PolicyCoverageDto>(customerBasicCoverage || {});

    // Update form data when the coverage prop changes
    useEffect(() => {
        loadLookupData();
        if(policyDetails && policyDetails.policyLineOfBusinesses) {
            const lob = policyDetails.policyLineOfBusinesses.find(l => l.polEventId === polEventId);
            if (lob && lob.policyCoverages) {
                const coverage = lob.policyCoverages.find(c => c.id === coverageId);
                if (coverage) {
                    setCoverageData(coverage);
                }
            }
        }
    }, [coverageId]);

      useEffect(() => {
      if (coverageData?.coverageCode) {
          ensureCoverageCodeInOptions(coverageData.coverageCode);
      }
  }, [coverageId]);

    // Load lookup data for dropdowns
    const loadLookupData = async () => {
        try {
            // Load coverage types
            const coverageTypesResponse = await policyDetailService.fetchCoverageCodeDropdowns(isCoverage);
            const formattedCoverageTypes = coverageTypesResponse?.map(item => ({
                    label: item.description,
                    value: item.description
                })) || [];
            setCoverageTypes(formattedCoverageTypes);
            
            // Load deductible types
            setDeductibleTypes(deductibleTypesOptions || []);
        } catch (error) {
            console.error("Error loading lookup data:", error);
            toast.error("Failed to load dropdown options");
        }
    };

      const ensureCoverageCodeInOptions = (coverageCode: string) => {
      if (!coverageCode) return;
      
      const updatedCoverageCodes = coverageTypes;
      if (!updatedCoverageCodes) return [];

      // Check if the code already exists in options
      const codeExists = updatedCoverageCodes.some(option => option.label === coverageCode);
        if (!codeExists) {
            // If not, add it to the options
            updatedCoverageCodes.push({
                label: coverageCode,
                value: coverageCode
            });
            setCoverageTypes([...updatedCoverageCodes]);
        }
  };

    const onEntityFieldChange = (propName: string, value: string | number | null) => {
        const updatedData = {
            ...coverageData,
            [propName]: value,
        };
        setCoverageData(updatedData);
        
        const updatedPolicyDetails = JSON.parse(JSON.stringify(policyDetails));
        
        if (updatedPolicyDetails && updatedPolicyDetails.policyLineOfBusinesses) {
            const updatedLOBs = updatedPolicyDetails.policyLineOfBusinesses.map(lob => {
                // Only update LOBs matching our policy event ID
                if (lob.polEventId === polEventId) {
                    // Check if the coverage exists
                    const coverageExists = lob.policyCoverages.some(cov => cov.id === coverageId);
                    
                    if (coverageExists) {
                        // If coverage exists, update it
                        return {
                            ...lob,
                            policyCoverages: lob.policyCoverages.map((cov: PolicyCoverageDto) => {
                                if (cov.id === coverageId) {
                                    return {
                                        ...cov,
                                        ...updatedData,
                                    };
                                } else {
                                    return cov;
                                }
                            }),
                        };
                    } else {
                        // If coverage doesn't exist, add it to the array
                        return {
                            ...lob,
                            policyCoverages: [
                                {
                                    id: coverageId,
                                    ...updatedData
                                },
                                ...lob.policyCoverages
                            ]
                        };
                    }
                }
                return lob;
            });
            
            updatedPolicyDetails.policyLineOfBusinesses = updatedLOBs;

            // Dispatch the updated policy details to the store
            console.log("Updated policy details:", updatedPolicyDetails);
            dispatch(updateSelectedPolicyDetails({ 
                policyId: polId, 
                polEventId: polEventId, 
                policyDetails: updatedPolicyDetails 
            }));
        }

        // Mark form as modified
        if (setIsFormSaved) {
            setIsFormSaved(false);
        }
    };

    return (
        <div className="main-cst-coverage-div">
            <Row>
                <Column lg={12} md={12}>
                    <Row className="mb-3">
                        <Column lg={6} md={6}
                        className={formValidation['coverageCode_' + coverageId]?.isValid == false ? 'feedback-row' : ''}>
                            <Select
                                id='coverageCode'
                                label="Coverage"
                                options={coverageTypes}
                                defaultSelected={coverageTypes?.filter(({ value }) =>
                                    value === coverageData.coverageCode
                                )}
                                onChange={(value) => {
                                    onEntityFieldChange('coverageCode', value.toString());
                                }}
                                placeholder="Select Coverage"
                                multiple={false}
                                className='mb-3'
                                isRequired={true}
                                tabIndex={tbIndex}
                                // isInvalid={formValidation['coverageCode']?.isValid === false}
                                // invalidWarning={formValidation['coverageCode']?.warningText}
                                isInvalid={formValidation['coverageCode_' + coverageId]?.isValid == false}
                            />
                            {formValidation['coverageCode_' + coverageId]?.isValid == false && (
                                <Form.Control.Feedback type="invalid">
                                    {formValidation['coverageCode_' + coverageId]?.warningText}
                                </Form.Control.Feedback>
                            )}
                        </Column>
                        <Column lg={2} md={2}>
                            <FloatingLabel
                                controlId="limit1"
                                className="mb-3"
                                label="Limit 1"
                            >
                                <Form.Control
                                    type="text"
                                tabIndex={tbIndex}
                                    name="limit1"
                                    placeholder="Limit 1"
                                    value={coverageData.limit1 || ''}
                                    onChange={(event) => onEntityFieldChange('limit1', event.target.value ? parseFloat(event.target.value) : 0)}
                                    // maxLength={30}
                                    isInvalid={formValidation['limit1']?.isValid === false}
                                />
                                {formValidation['limit1']?.isValid === false && (
                                    <Form.Control.Feedback type="invalid">
                                        {formValidation['limit1']?.warningText}
                                    </Form.Control.Feedback>
                                )}
                            </FloatingLabel>
                        </Column>
                        <Column lg={2} md={2}>
                            <FloatingLabel
                                controlId="limit2"
                                className="mb-3"
                                label="Limit 2"
                            >
                                <Form.Control
                                    type="text"
                                tabIndex={tbIndex}
                                    name="limit2"
                                    placeholder="Limit 2"
                                    value={coverageData.limit2 || ''}
                                    onChange={(event) => onEntityFieldChange('limit2', event.target.value ? parseFloat(event.target.value) : 0)}
                                    // isInvalid={formValidation['limit2']?.isValid === false}
                                />
                            </FloatingLabel>
                        </Column>
                        {isEarthquake ? (
                            <Column lg={2} md={2}>
                                <FloatingLabel
                                    controlId="deductible1"
                                    className="mb-3"
                                    label="Earthquake (%)"
                                >
                                    <Form.Control
                                        type="number"
                                        name="deductible1"
                                        placeholder="Earthquake (%)"
                                        value={coverageData.deductible1 || ''}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            if (val === '' || (parseFloat(val) >= 0 && parseFloat(val) <= 100)) {
                                                onEntityFieldChange('deductible1', val ? parseFloat(val) : null);
                                            }
                                        }}
                                        min="0"
                                        max="100"
                                        step="0.1"
                                        tabIndex={tbIndex}
                                    />
                                </FloatingLabel>
                            </Column>
                        ) : (
                            <Column lg={2} md={2}>
                                <FloatingLabel
                                    controlId="fullTermPremium"
                                    className="mb-3"
                                    label="Premium ($)"
                                >
                                    <Form.Control
                                        type="number"
                                        tabIndex={tbIndex}
                                        name="fullTermPremium"
                                        placeholder="Premium ($)"
                                        value={coverageData.fullTermPremium || ''}
                                        onChange={(event) => onEntityFieldChange('fullTermPremium', event.target.value ? parseFloat(event.target.value) : 0)}
                                    />
                                </FloatingLabel>
                            </Column>
                        )}
                    </Row>

                    {/* Second row - Deductible Types */}
                    {isEarthquake ? (
                        <Row className="mb-3">
                            <Column lg={2} md={2}>
                                <FloatingLabel
                                    controlId="fullTermPremium"
                                    className="mb-3"
                                    label="Premium ($)"
                                >
                                    <Form.Control
                                        type="number"
                                        tabIndex={tbIndex}
                                        name="fullTermPremium"
                                        placeholder="Premium ($)"
                                        value={coverageData.fullTermPremium || ''}
                                        onChange={(event) => onEntityFieldChange('fullTermPremium', event.target.value ? parseFloat(event.target.value) : 0)}
                                    />
                                </FloatingLabel>
                            </Column>
                        </Row>
                    ) : (
                        <>                
                    <Row className="mb-3">
                        <Column lg={4} md={4}>
                            <Select
                                id='deductibleType1'
                                label="All Peril"
                                options={deductibleTypes}
                                tabIndex={tbIndex}
                                defaultSelected={deductibleTypes?.filter(({ value }) =>
                                    value === coverageData.deductibleType1
                                )}
                                onChange={(value) => {
                                    onEntityFieldChange('deductibleType1',  value != null && value.toString() !== '' ? value.toString() : null);
                                }}
                                placeholder="Select Type"
                                multiple={false}
                                className='mb-3'
                                // isInvalid={formValidation['deductibleType1']?.isValid === false}
                                // invalidWarning={formValidation['deductibleType1']?.warningText}
                            />
                        </Column>
                        {isCoverage ? (
                        <Column lg={4} md={4}>
                            <Select
                                id='deductibleType2'
                                label="Wind/Hail"
                                options={deductibleTypes}
                                tabIndex={tbIndex}
                                defaultSelected={deductibleTypes?.filter(({ value }) =>
                                    value === coverageData.deductibleType2
                                )}
                                onChange={(value) => {
                                    onEntityFieldChange('deductibleType2',  value != null && value.toString() !== '' ? value.toString() : null);
                                }}
                                placeholder="Select Type"
                                multiple={false}
                                className='mb-3'
                                // isInvalid={formValidation['deductibleType2']?.isValid === false}
                                // invalidWarning={formValidation['deductibleType2']?.warningText}
                            />
                        </Column>
                        ) : null}
                        {isCoverage ? (
                        <Column lg={4} md={4}>
                            <Select
                                id='deductibleType3'
                                label="Theft"
                                options={deductibleTypes}
                                tabIndex={tbIndex}
                                defaultSelected={deductibleTypes?.filter(({ value }) =>
                                    value === coverageData.deductibleType3
                                )}
                                onChange={(value) => {
                                    onEntityFieldChange('deductibleType3',  value != null && value.toString() !== '' ? value.toString() : null);
                                }}
                                placeholder="Select Type"
                                multiple={false}
                                className='mb-3'
                                // isInvalid={formValidation['deductibleType3']?.isValid === false}
                                // invalidWarning={formValidation['deductibleType3']?.warningText}
                            />
                        </Column>
                        ) : null}
                    </Row>
                    <Row className="mb-3">
                        <Column lg={4} md={4}>
                            <FloatingLabel
                                controlId="deductible1"
                                className="mb-3"
                                label="Amount 1"
                            >
                                <Form.Control
                                    type="number"
                                tabIndex={tbIndex}
                                    name="deductible1"
                                    placeholder="Deductible 1"
                                    value={coverageData.deductible1}
                                    onChange={(e) => onEntityFieldChange('deductible1', e.target.value ? parseFloat(e.target.value) : null)}
                                />
                            </FloatingLabel>
                        </Column>
                        {isCoverage ? (
                            <Column lg={4} md={4}>
                                <FloatingLabel
                                    controlId="deductible2"
                                    className="mb-3"
                                    label="Amount 2"
                                >
                                    <Form.Control
                                        type="number"
                                tabIndex={tbIndex}
                                        name="deductible2"
                                        placeholder="Deductible 2"
                                        value={coverageData.deductible2}
                                        onChange={(e) => onEntityFieldChange('deductible2', e.target.value ? parseFloat(e.target.value) : null)}
                                    />
                                </FloatingLabel>
                            </Column>
                        ) : null}
                        {isCoverage ? (
                            <Column lg={4} md={4}>
                                <FloatingLabel
                                    controlId="deductible3"
                                    className="mb-3"
                                    label="Amount 3"
                                >
                                    <Form.Control
                                        type="number"
                                tabIndex={tbIndex}
                                        name="deductible3"
                                        placeholder="Deductible 3"
                                        value={coverageData.deductible3}
                                        onChange={(e) => onEntityFieldChange('deductible3', e.target.value ? parseFloat(e.target.value) : null)}
                                    />
                                </FloatingLabel>
                            </Column>
                        ) : null}
                    </Row>
                   </>
                    )}
                </Column>
            </Row>
        </div>
    );
};

export default CustomerBasicCoverageForm;